import React, { useCallback } from "react";
import _ from "lodash";

import {
  DeleteOutlineOutlinedIcon,
  DescriptionOutlinedIcon,
  VisibilityOutlinedIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { format, parseJSON } from "utils/datetime";

import IconButton from "components/IconButton";
import Stack from "components/Stack";
import Text from "components/Text";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { IPackageDetailsFieldValue } from "entities/Checklist/sdk";
import { IFile } from "entities/File/sdk";

export type IFilefieldCustomValue = Pick<
  IPackageDetailsFieldValue,
  "value_id" | "file"
> & {
  uploaded_at?: string;
  uploaded_by?: { email: string };
};
interface IFilesTable {
  customFieldValues: IFilefieldCustomValue[];
  onDownload: ({ fileId }: { fileId: number }) => void;
  onDelete?: ({ customValue }: { customValue: IFilefieldCustomValue }) => void;
}

const FilesTable: React.FC<IFilesTable> = ({
  customFieldValues,
  onDownload,
  onDelete,
}) => {
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const onDeleteFile = useCallback(
    (customValue: IFilefieldCustomValue) => {
      onDelete && onDelete({ customValue });
    },
    [onDelete]
  );

  const handleDeleteFile = useCallback(
    (customValue: IFilefieldCustomValue) => {
      showConfirmationDialog({
        onConfirm: () => onDeleteFile(customValue),
        message: "Are you sure you want to delete this file?",
      });
    },
    [onDeleteFile, showConfirmationDialog]
  );

  const handleDownloadClick = useCallback(
    ({ file }: { file: IFile }) => {
      onDownload && onDownload({ fileId: file.id });
    },
    [onDownload]
  );

  return (
    <>
      {customFieldValues.map(({ file, uploaded_at, value_id, uploaded_by }) => (
        <React.Fragment key={value_id}>
          {!_.isNil(file) && (
            <Stack
              key={value_id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{
                padding: theme.spacing(0.5),
                borderBottom: `1px solid ${colors.blue20}`,
              }}
              data-testid="uploaded-file"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <DescriptionOutlinedIcon
                  sx={{ width: "16px", height: "16px" }}
                />
                <Text
                  variant="text4"
                  sx={{ flex: 1 }}
                  title={file?.original_file_name}
                  data-testid="uploaded-file-name"
                >
                  {_.truncate(file?.original_file_name, { length: 30 })}
                </Text>
              </Stack>
              <Stack direction="row" spacing={3} alignItems="center">
                {uploaded_by && (
                  <Text
                    variant="text4"
                    data-testid="file-uploaded-by"
                    title={uploaded_by?.email}
                  >
                    {_.truncate(uploaded_by?.email, { length: 20 })}
                  </Text>
                )}
                <Text variant="text4" data-testid="file-uploaded-datetime">
                  {uploaded_at &&
                    format(parseJSON(uploaded_at), "MMM dd, yyyy, hh:mm a")}
                </Text>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  data-testid="file-action-buttons"
                >
                  <IconButton
                    size="small"
                    title="Preview"
                    onClick={() => handleDownloadClick({ file })}
                    dataTestid="view-file-button"
                  >
                    <VisibilityOutlinedIcon
                      sx={{ width: "20px", height: "20px" }}
                    />
                  </IconButton>
                  {onDelete && (
                    <IconButton
                      size="small"
                      title="Delete"
                      onClick={() =>
                        handleDeleteFile({ file, uploaded_at, value_id })
                      }
                      dataTestid="delete-file-button"
                    >
                      <DeleteOutlineOutlinedIcon
                        sx={{ width: "20px", height: "20px" }}
                      />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Stack>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

export default FilesTable;
